import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaLock, FaShieldAlt, FaCamera, FaSave } from 'react-icons/fa';
import { getAccount, updateAccount, changePassword, enable2FA, disable2FA, uploadProfilePhoto, getImageUrl } from '../services/api';

const AccountSettings = () => {
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: ''
  });
  
  // Password data
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // 2FA data
  const [twoFAData, setTwoFAData] = useState({
    enabled: false,
    secret: '',
    qr_uri: ''
  });
  
  const [verificationCode, setVerificationCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');

  useEffect(() => {
    fetchAccountData();
  }, []);

  const fetchAccountData = async () => {
    try {
      const response = await getAccount();
      setProfileData({
        name: response.name,
        email: response.email,
        phone: response.phone || '',
        bio: response.bio || ''
      });
      setTwoFAData({
        enabled: response.two_factor_enabled,
        secret: '',
        qr_uri: ''
      });
    } catch (error) {
      console.error('Error fetching account data:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateAccount(profileData);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });

      const updatedUser = { ...user, name: profileData.name };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Update failed' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (passwordData.new_password !== passwordData.confirm_password) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    setLoading(true);

    try {
      await changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Password change failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await enable2FA();
      setTwoFAData({
        enabled: true,
        secret: response.secret,
        qr_uri: response.qr_uri
      });
      setMessage({ type: 'success', text: '2FA enabled! Scan the QR code with your authenticator app.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to enable 2FA' });
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setMessage({ type: 'error', text: 'Please enter your password' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await disable2FA({ password: disablePassword });
      setTwoFAData({ enabled: false, secret: '', qr_uri: '' });
      setDisablePassword('');
      setMessage({ type: 'success', text: '2FA disabled successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to disable 2FA' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('photo', file);

    try {
      const response = await uploadProfilePhoto(formData);
      setMessage({ type: 'success', text: 'Profile photo updated successfully!' });

      const updatedUser = { ...user, profile_photo: response.profile_photo };
      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Upload failed' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '900px', padding: '2rem' }}>
      <h1 style={{ color: 'var(--primary-orange)', marginBottom: '2rem' }}>
        Account Settings
      </h1>

      {message.text && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid var(--light-gray)' }}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === 'profile' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === 'profile' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === 'profile' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaUser /> Profile
        </button>
        <button
          onClick={() => setActiveTab('password')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === 'password' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === 'password' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === 'password' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaLock /> Password
        </button>
        <button
          onClick={() => setActiveTab('2fa')}
          style={{
            padding: '1rem 2rem',
            background: activeTab === '2fa' ? 'var(--primary-orange)' : 'transparent',
            color: activeTab === '2fa' ? 'white' : 'var(--dark-gray)',
            border: 'none',
            borderBottom: activeTab === '2fa' ? '3px solid var(--primary-orange)' : 'none',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          <FaShieldAlt /> Two-Factor Auth
        </button>
      </div>

      {activeTab === 'profile' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Profile Information</h2>
          
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {user?.profile_photo ? (
                <img
                  src={getImageUrl(user.profile_photo)}
                  alt="Profile"
                  style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  background: 'var(--light-gray)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  color: 'var(--gray)'
                }}>
                  <FaUser />
                </div>
              )}
              <label
                htmlFor="profilePhotoInput"
                style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  background: 'var(--primary-orange)',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}
              >
                <FaCamera />
              </label>
              <input
                id="profilePhotoInput"
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          <form onSubmit={handleProfileUpdate}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={profileData.email}
                disabled
                style={{ background: 'var(--off-white)', cursor: 'not-allowed' }}
              />
              <small style={{ color: 'var(--gray)' }}>Email cannot be changed</small>
            </div>

            <div className="form-group">
              <label>Phone (Optional)</label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                placeholder="+1 234 567 8900"
              />
            </div>

            <div className="form-group">
              <label>Bio (Optional)</label>
              <textarea
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                rows="4"
                placeholder="Tell us about yourself..."
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              <FaSave /> {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'password' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Change Password</h2>
          
          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label>Current Password</label>
              <input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
              />
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              <FaLock /> {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      )}

      {activeTab === '2fa' && (
        <div className="auth-container" style={{ maxWidth: '100%' }}>
          <h2 style={{ color: 'var(--primary-green)' }}>Two-Factor Authentication</h2>
          
          {!twoFAData.enabled ? (
            <div>
              <p style={{ marginBottom: '1.5rem' }}>
                Add an extra layer of security to your account by enabling two-factor authentication.
              </p>
              <button onClick={handleEnable2FA} className="btn-submit" disabled={loading}>
                <FaShieldAlt /> Enable 2FA
              </button>
            </div>
          ) : (
            <div>
              <div style={{ padding: '1rem', background: 'var(--light-green)', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <strong style={{ color: 'var(--dark-green)' }}>✓ Two-Factor Authentication is enabled</strong>
              </div>

              {twoFAData.qr_uri && (
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <h3>Scan this QR code with your authenticator app:</h3>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFAData.qr_uri)}`}
                    alt="2FA QR Code"
                    style={{ margin: '1rem auto' }}
                  />
                  <p style={{ fontSize: '0.9rem', color: 'var(--gray)' }}>
                    Secret: <code>{twoFAData.secret}</code>
                  </p>
                </div>
              )}

              <div className="form-group">
                <label>Enter your password to disable 2FA</label>
                <input
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Your password"
                />
              </div>

              <button
                onClick={handleDisable2FA}
                className="btn-submit"
                disabled={loading}
                style={{ background: '#f44336' }}
              >
                Disable 2FA
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AccountSettings;
